import PageComponent from "../components/page/PageComponent.vue";
import SectionSheet from "../components/section_sheet/SectionSheet.vue";
import PageControl from "../components/page_control/PageControl.vue";
import { format_date } from "@/utils/date_formatter";

export default {
  name: "BankStatementVerificationReport",
  components: { PageControl, PageComponent, SectionSheet },
  props: {
    sheet_elements: {
      type: String,
      default: ".sheet",
      required: false,
    },
    bank_statement_data: {
      type: Object,
      default: () => ({}),
      required: false,
    },
  },
  data() {
    return {
      export_options: {
        account_information: false,
        transaction_analysis: false,
        transaction_history: false,
        monthly_breakdown: false,
        category_analysis: false,
        raw_json: false,
      },
      export_format: "csv",
      libraries_loaded: {
        papa_parse: false,
        sheetjs: false,
        apexcharts: false,
      },
    };
  },
  computed: {
    safe_bank_statement_data() {
      return this.bank_statement_data || {};
    },

    // Account Data
    account_data() {
      return this.safe_bank_statement_data.data?.[0] || {};
    },

    // Report metadata
    report_reference() {
      return `BSV-${Date.now().toString().slice(-8)}`;
    },

    generated_date() {
      return new Date().toLocaleDateString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },

    // Account Information
    account_id() {
      return this.account_data.id || "N/A";
    },

    account_number() {
      return this.account_data.accountNumber || "N/A";
    },

    account_name() {
      return this.account_data.accountName || "N/A";
    },

    account_balance() {
      return parseFloat(this.account_data.accountBalance || 0);
    },

    formatted_account_balance() {
      return this.format_currency(this.account_balance);
    },

    overdraft_limit() {
      return this.account_data.overdraftLimit || "N/A";
    },

    account_holder() {
      return this.account_data.accountHolder || "N/A";
    },

    account_address() {
      return this.account_data.accountAddress || "N/A";
    },

    // Transaction Data
    all_transactions() {
      return this.account_data.transactions || [];
    },
    // --- PAGINATION LOGIC FOR RECENT TRANSACTIONS ---
    recent_transactions() {
      // last 200, most recent first
      return this.all_transactions.slice(-200).reverse();
    },
    total_recent_transactions() {
      return this.recent_transactions.length;
    },
    paginated_recent_transactions() {
      // Array of arrays, each with 25 transactions
      const pages = [];
      for (let i = 0; i < this.recent_transactions.length; i += 25) {
        pages.push(this.recent_transactions.slice(i, i + 25));
      }
      return pages;
    },

    total_transactions() {
      return this.all_transactions.length;
    },

    // Financial Calculations
    total_credits() {
      return this.all_transactions
        .filter((t) => t.type === "credit")
        .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
    },

    formatted_total_credits() {
      return this.format_currency(this.total_credits);
    },

    total_credit_transactions() {
      return this.all_transactions.filter((t) => t.type === "credit").length;
    },

    total_debits() {
      return this.all_transactions
        .filter((t) => t.type === "debit")
        .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
    },

    formatted_total_debits() {
      return this.format_currency(this.total_debits);
    },

    total_debit_transactions() {
      return this.all_transactions.filter((t) => t.type === "debit").length;
    },

    net_flow() {
      return this.total_credits - this.total_debits;
    },

    formatted_net_flow() {
      return this.format_currency(Math.abs(this.net_flow));
    },

    // Balance Analysis
    opening_balance() {
      if (this.all_transactions.length === 0) return 0;
      const first_transaction = this.all_transactions[0];
      return (
        first_transaction.balance -
        (first_transaction.type === "credit"
          ? first_transaction.amount
          : -first_transaction.amount)
      );
    },

    formatted_opening_balance() {
      return this.format_currency(this.opening_balance);
    },

    closing_balance() {
      if (this.all_transactions.length === 0) return this.account_balance;
      return this.all_transactions[this.all_transactions.length - 1].balance;
    },

    formatted_closing_balance() {
      return this.format_currency(this.closing_balance);
    },

    highest_balance() {
      if (this.all_transactions.length === 0) return this.account_balance;
      return Math.max(...this.all_transactions.map((t) => t.balance));
    },

    formatted_highest_balance() {
      return this.format_currency(this.highest_balance);
    },

    lowest_balance() {
      if (this.all_transactions.length === 0) return this.account_balance;
      return Math.min(...this.all_transactions.map((t) => t.balance));
    },

    formatted_lowest_balance() {
      return this.format_currency(this.lowest_balance);
    },

    average_balance() {
      if (this.all_transactions.length === 0) return this.account_balance;
      const total_balance = this.all_transactions.reduce(
        (sum, t) => sum + t.balance,
        0
      );
      return total_balance / this.all_transactions.length;
    },

    formatted_average_balance() {
      return this.format_currency(this.average_balance);
    },

    // Date Analysis
    statement_start_date() {
      if (this.all_transactions.length === 0) return null;
      return new Date(this.all_transactions[0].date);
    },

    statement_end_date() {
      if (this.all_transactions.length === 0) return null;
      return new Date(
        this.all_transactions[this.all_transactions.length - 1].date
      );
    },

    formatted_statement_period() {
      if (!this.statement_start_date || !this.statement_end_date) return "N/A";
      return `${this.formatDate(
        this.statement_start_date
      )} - ${this.formatDate(this.statement_end_date)}`;
    },

    statement_period_days() {
      if (!this.statement_start_date || !this.statement_end_date) return 0;
      const diff_time = Math.abs(
        this.statement_end_date - this.statement_start_date
      );
      return Math.ceil(diff_time / (1000 * 60 * 60 * 24));
    },

    active_transaction_days() {
      if (this.all_transactions.length === 0) return 0;
      const unique_dates = new Set(
        this.all_transactions.map((t) => new Date(t.date).toDateString())
      );
      return unique_dates.size;
    },

    average_daily_transactions() {
      if (this.statement_period_days === 0) return 0;
      return (this.total_transactions / this.statement_period_days).toFixed(1);
    },

    // Category Analysis
    transaction_categories() {
      const categories = {};

      this.all_transactions.forEach((transaction) => {
        const category_name = transaction.category || "Uncategorized";
        const category_slug = transaction.category_slug || "uncategorized";

        if (!categories[category_slug]) {
          categories[category_slug] = {
            name: category_name,
            slug: category_slug,
            count: 0,
            total_credits: 0,
            total_debits: 0,
          };
        }

        categories[category_slug].count++;

        if (transaction.type === "credit") {
          categories[category_slug].total_credits += parseFloat(
            transaction.amount || 0
          );
        } else {
          categories[category_slug].total_debits += parseFloat(
            transaction.amount || 0
          );
        }
      });

      return Object.values(categories).sort((a, b) => b.count - a.count);
    },

    // Export Options
    has_export_options_selected() {
      return Object.values(this.export_options).some((option) => option);
    },
    modal_instance() {
      const modal_id = `export-modal-${this.report_reference}`;
      const modal_element = document.getElementById(modal_id);
      if (!modal_element) {
        console.error(`Modal element with ID ${modal_id} not found.`);
        return;
      }
      const modal = new window.bootstrap.Modal(modal_element);
      return modal;
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.renderCharts();
    });
  },
  methods: {
    createIncomeExpensesChart() {
      const data = this.getMonthlyIncomeExpenses();

      const options = {
        chart: {
          type: "bar",
          height: 350,
          stacked: false,
          toolbar: {
            show: false,
          },
        },
        series: [
          {
            name: "Income",
            data: data.map((month) => month.income),
          },
          {
            name: "Expenses",
            data: data.map((month) => month.expenses),
          },
        ],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "60%",
            borderRadius: 4,
          },
        },
        colors: ["#00E396", "#FF4560"],
        dataLabels: {
          enabled: false,
        },
        xaxis: {
          categories: data.map((month) => month.month),
        },
        yaxis: {
          title: {
            text: "Amount (R)",
          },
          labels: {
            formatter: (value) => `R ${this.format_currency(value)}`,
          },
        },
        tooltip: {
          y: {
            formatter: (value) => `R ${this.format_currency(value)}`,
          },
        },
        legend: {
          position: "top",
        },
      };

      return options;
    },

    // Helper method to aggregate monthly data
    getMonthlyIncomeExpenses() {
      const months = {};

      this.all_transactions.forEach((transaction) => {
        const date = new Date(transaction.date);
        const monthYear = `${date.toLocaleString("default", {
          month: "short",
        })} ${date.getFullYear()}`;

        if (!months[monthYear]) {
          months[monthYear] = { month: monthYear, income: 0, expenses: 0 };
        }

        if (transaction.type === "credit") {
          months[monthYear].income += parseFloat(transaction.amount || 0);
        } else {
          months[monthYear].expenses += parseFloat(transaction.amount || 0);
        }
      });

      return Object.values(months).sort((a, b) => {
        const [aMonth, aYear] = a.month.split(" ");
        const [bMonth, bYear] = b.month.split(" ");
        return (
          new Date(`${aMonth} 1, ${aYear}`) - new Date(`${bMonth} 1, ${bYear}`)
        );
      });
    },
    createCategoriesChart() {
      const options = {
        chart: {
          type: "donut",
          height: 350,
        },
        series: this.transaction_categories.map((category) => category.count),
        labels: this.transaction_categories.map((category) => category.name),
        colors: [
          "#3366ff",
          "#33CC99",
          "#FF6384",
          "#FFCD56",
          "#36A2EB",
          "#9966FF",
        ],
        plotOptions: {
          pie: {
            donut: {
              size: "65%",
              labels: {
                show: true,
                name: {
                  show: true,
                },
                value: {
                  show: true,
                  formatter: (val) => val,
                },
                total: {
                  show: true,
                  label: "Total",
                  formatter: (w) => {
                    return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                  },
                },
              },
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
        legend: {
          position: "bottom",
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                height: 300,
              },
              legend: {
                position: "bottom",
              },
            },
          },
        ],
      };

      return options;
    },

    // Apex Charts methods
    async renderCharts() {
      await this.load_apexcharts();

      const incomeExpensesChart = new window.ApexCharts(
        document.querySelector("#income-expenses-chart"),
        this.createIncomeExpensesChart()
      );

      const categoriesChart = new window.ApexCharts(
        document.querySelector("#categories-chart"),
        this.createCategoriesChart()
      );

      // Render charts
      incomeExpensesChart.render();
      categoriesChart.render();
    },

    // Utility methods
    format_currency(amount) {
      if (amount == null || isNaN(amount)) return "0.00";
      return parseFloat(amount).toFixed(2);
    },
    formatDate(date) {
      return format_date(date);
    },
    get_category_icon(category_slug) {
      const icon_map = {
        transfers: "bi bi-arrow-left-right",
        income: "bi bi-arrow-down-circle",
        expenses: "bi bi-arrow-up-circle",
        "salary-wages": "bi bi-cash-coin",
        uncategorized: "bi bi-question-circle",
      };
      return icon_map[category_slug] || "bi bi-circle";
    },

    // Export functionality
    show_export_modal() {
      this.modal_instance.show();
    },
    hide_export_modal() {
      this.modal_instance.hide();
    },
    async load_export_libraries() {
      if (this.export_format === "csv" && !this.libraries_loaded.papa_parse) {
        await this.load_papa_parse();
      } else if (
        this.export_format === "excel" &&
        !this.libraries_loaded.sheetjs
      ) {
        await this.load_sheetjs();
      }
      // No additional library needed for JSON export
      if (!this.libraries_loaded.apexcharts) {
        await this.load_apexcharts();
      }
    },
    async load_apexcharts() {
      return new Promise((resolve, reject) => {
        if (typeof window.ApexCharts !== "undefined") {
          this.libraries_loaded.apexcharts = true;
          resolve();
          return;
        }
        const script = document.createElement("script");
        script.src =
          "https://cdn.jsdelivr.net/npm/apexcharts@3.35.3/dist/apexcharts.min.js";
        script.onload = () => {
          this.libraries_loaded.apexcharts = true;
          resolve();
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    async load_papa_parse() {
      return new Promise((resolve, reject) => {
        if (typeof window.Papa !== "undefined") {
          this.libraries_loaded.papa_parse = true;
          resolve();
          return;
        }
        const script = document.createElement("script");
        script.src =
          "https://cdn.jsdelivr.net/npm/papaparse@5.4.1/papaparse.min.js";
        script.onload = () => {
          this.libraries_loaded.papa_parse = true;
          resolve();
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    async load_sheetjs() {
      return new Promise((resolve, reject) => {
        if (typeof window.XLSX !== "undefined") {
          this.libraries_loaded.sheetjs = true;
          resolve();
          return;
        }
        const script = document.createElement("script");
        script.src =
          "https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js";
        script.onload = () => {
          this.libraries_loaded.sheetjs = true;
          resolve();
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    async export_data() {
      try {
        await this.load_export_libraries();
        const export_data = this.prepare_export_data();
        if (this.export_format === "csv") {
          this.export_as_csv(export_data);
        } else if (this.export_format === "excel") {
          this.export_as_excel(export_data);
        } else if (this.export_format === "json") {
          this.export_as_json(export_data);
        }
        this.hide_export_modal();
      } catch (error) {
        console.error("Export failed:", error);
        alert("Export failed. Please try again.");
      }
    },
    prepare_export_data() {
      const export_data = {};

      if (this.export_options.account_information) {
        export_data.account_information = [
          {
            report_reference: this.report_reference,
            generated_date: this.generated_date,
            account_id: this.account_id,
            account_number: this.account_number,
            account_name: this.account_name,
            account_holder: this.account_holder,
            account_address: this.account_address,
            current_balance: this.formatted_account_balance,
            overdraft_limit: this.overdraft_limit,
            statement_period: this.formatted_statement_period,
            statement_period_days: this.statement_period_days,
          },
        ];
      }

      if (this.export_options.transaction_analysis) {
        export_data.financial_summary = [
          {
            report_reference: this.report_reference,
            total_transactions: this.total_transactions,
            total_credits: this.formatted_total_credits,
            total_credit_transactions: this.total_credit_transactions,
            total_debits: this.formatted_total_debits,
            total_debit_transactions: this.total_debit_transactions,
            net_flow: this.formatted_net_flow,
            net_flow_type: this.net_flow >= 0 ? "positive" : "negative",
            opening_balance: this.formatted_opening_balance,
            closing_balance: this.formatted_closing_balance,
            highest_balance: this.formatted_highest_balance,
            lowest_balance: this.formatted_lowest_balance,
            average_balance: this.formatted_average_balance,
            statement_period_days: this.statement_period_days,
            active_transaction_days: this.active_transaction_days,
            average_daily_transactions: this.average_daily_transactions,
            generated_date: this.generated_date,
          },
        ];
      }

      if (this.export_options.category_analysis) {
        export_data.transaction_categories = this.transaction_categories.map(
          (category) => ({
            category_name: category.name,
            category_slug: category.slug,
            transaction_count: category.count,
            total_credits: this.format_currency(category.total_credits),
            total_debits: this.format_currency(category.total_debits),
            net_amount: this.format_currency(
              category.total_credits - category.total_debits
            ),
            percentage_of_total:
              ((category.count / this.total_transactions) * 100).toFixed(2) +
              "%",
            report_reference: this.report_reference,
          })
        );
      }

      if (this.export_options.monthly_breakdown) {
        export_data.monthly_analysis = this.getMonthlyIncomeExpenses().map(
          (month) => ({
            month: month.month,
            income: month.income,
            expenses: month.expenses,
            net_flow: month.income - month.expenses,
            formatted_income: this.format_currency(month.income),
            formatted_expenses: this.format_currency(month.expenses),
            formatted_net_flow: this.format_currency(
              month.income - month.expenses
            ),
            report_reference: this.report_reference,
          })
        );
      }

      if (this.export_options.transaction_history) {
        export_data.transaction_history = this.all_transactions.map(
          (t, index) => ({
            transaction_number: index + 1,
            transaction_id: t.id || "N/A",
            date: this.formatDate(t.date),
            description: t.description || "N/A",
            transaction_type: t.type,
            amount: t.amount,
            formatted_amount: this.format_currency(t.amount),
            balance_after: t.balance,
            formatted_balance: this.format_currency(t.balance),
            category: t.category || "Uncategorized",
            category_slug: t.category_slug || "uncategorized",
            report_reference: this.report_reference,
          })
        );
      }

      if (this.export_options.raw_json) {
        export_data.raw_data = {
          original_bank_statement_data: this.safe_bank_statement_data,
          report_metadata: {
            report_reference: this.report_reference,
            generated_date: this.generated_date,
            export_timestamp: new Date().toISOString(),
            total_transactions_processed: this.total_transactions,
            data_completeness: {
              has_account_data: !!this.account_data,
              has_transactions: this.all_transactions.length > 0,
              has_balance_data: this.account_balance !== null,
            },
          },
        };
      }

      return export_data;
    },
    export_as_csv(export_data) {
      Object.keys(export_data).forEach((sheet_name) => {
        const csv_content = window.Papa.unparse(export_data[sheet_name]);
        this.download_file(
          csv_content,
          `${sheet_name}_${this.report_reference}.csv`,
          "text/csv"
        );
      });
    },
    export_as_excel(export_data) {
      const workbook = window.XLSX.utils.book_new();
      Object.keys(export_data).forEach((sheet_name) => {
        const worksheet = window.XLSX.utils.json_to_sheet(
          export_data[sheet_name]
        );
        window.XLSX.utils.book_append_sheet(workbook, worksheet, sheet_name);
      });
      const excel_buffer = window.XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([excel_buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      this.download_blob(
        blob,
        `bank_statement_report_${this.report_reference}.xlsx`
      );
    },
    export_as_json(export_data) {
      const json_content = JSON.stringify(export_data, null, 2);
      this.download_file(
        json_content,
        `bank_statement_report_${this.report_reference}.json`,
        "application/json"
      );
    },
    download_file(content, filename, content_type) {
      const blob = new Blob([content], { type: content_type });
      this.download_blob(blob, filename);
    },
    download_blob(blob, filename) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    },
  },
};
